import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import '../styles/SecretPage.css';

const SecretPage = ({ apiBaseUrl }) => {
  const [mediaItems, setMediaItems] = useState([]);
  const [bpm, setBpm] = useState(125);
  const [emptyBeats, setEmptyBeats] = useState(16);
  const [revealedTiles, setRevealedTiles] = useState([]);
  const [started, setStarted] = useState(false);
  const audioRef = useRef(new Audio('/audio/secret-song.mp3'));
  const recordRef = useRef(null);
  const armRef = useRef(null);
  const gridRef = useRef(null);
  const beatTimerRef = useRef(null);
  const navigate = useNavigate();

  // Configurable settings
  const GRID_SIZE = 50; // Fixed ~50x50 grid
  const INITIAL_VISIBLE_TILES = 7; // Start showing ~7x7
  const ZOOM_DELAY = 10 * 1000; // 10 seconds
  const ZOOM_DURATION = 60 * 1000; // 1 minutes
  const INITIAL_SCALE = 1.5; // Adjusted for ~7x7 tiles

  useEffect(() => {
    // Fetch media items
    fetch(`${apiBaseUrl}/secret`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setMediaItems(data.media_items);
        setBpm(data.bpm || 125);
        setEmptyBeats(data.empty_beats || 16);
      })
      .catch(err => console.error('Error fetching secret media:', err));

    // Cleanup
    return () => {
      audioRef.current.pause();
      if (beatTimerRef.current) clearInterval(beatTimerRef.current);
    };
  }, [apiBaseUrl]);

  const beatInterval = 60000 / bpm;
  let beatCount = 0;
  let armRotation = 0;

  const revealNextTile = () => {
    beatCount++;

    // Move record arm inward
    if (beatCount % 2 === 0 && armRotation < 30 && armRef.current) {
      armRotation += 0.2;
      armRef.current.style.transform = `rotate(${100 + armRotation}deg)`;
    }

    if (beatCount <= emptyBeats) return;

    setRevealedTiles(prev => {
      const hiddenTileIndices = Array.from({ length: GRID_SIZE * GRID_SIZE })
        .map((_, index) => index)
        .filter(index => !prev.includes(index));

      if (hiddenTileIndices.length === 0) {
        if (beatTimerRef.current) clearInterval(beatTimerRef.current);
        return prev;
      }

      // Select a random tile within the viewport
      const visibleTiles = hiddenTileIndices.filter(index => {
        const tile = gridRef.current.children[index];
        if (!tile) return false;
        const rect = tile.getBoundingClientRect();
        const isVisible =
          rect.top >= -100 && // Tolerance for scaling
          rect.left >= -100 &&
          rect.bottom <= window.innerHeight + 100 &&
          rect.right <= window.innerWidth + 100;
        if (!isVisible) {
          console.log(`Tile ${index}: top=${rect.top}, bottom=${rect.bottom}, left=${rect.left}, right=${rect.right}`);
        }
        return isVisible;
      });

      if (visibleTiles.length === 0) {
        console.log('No visible tiles found');
        return prev;
      }

      const randomIndex = visibleTiles[Math.floor(Math.random() * visibleTiles.length)];
      return [...prev, randomIndex];
    });
  };

  const handleRecordClick = () => {
    if (started) return;
    setStarted(true);

    audioRef.current.play().catch(err => console.error('Audio play error:', err));
    if (recordRef.current) recordRef.current.classList.add('spinning');
    if (armRef.current) armRef.current.style.transform = `rotate(100deg)`;

    beatTimerRef.current = setInterval(revealNextTile, beatInterval);
  };

  // Generate tiles for GRID_SIZE x GRID_SIZE
  const tileCount = GRID_SIZE * GRID_SIZE;

  return (
    <div className="secret-container">
      <Helmet>
        <title>Y02 Media Database - Secret Easter Egg</title>
        <meta name="description" content="Discover a hidden gem in Y02 Media Database with this interactive media showcase." />
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="grid-wrapper">
        <div
          className="grid"
          ref={gridRef}
          style={{
            gridTemplateColumns: `repeat(${GRID_SIZE}, minmax(min(12vw, 12vh), 1fr))`,
            gridTemplateRows: `repeat(${GRID_SIZE}, minmax(min(12vw, 12vh), 1fr))`,
          }}
        >
          {Array.from({ length: tileCount }).map((_, index) => {
            const item = mediaItems[index % mediaItems.length] || {};
            return (
              <div
                key={index}
                className={`tile ${revealedTiles.includes(index) ? 'revealed' : ''}`}
                style={{ backgroundImage: item.image ? `url(${item.image})` : 'none' }}
                title={item.title ? `[${item.type}] ${item.title}` : ''}
                onClick={() => item.link && navigate(item.link)}
              ></div>
            );
          })}
        </div>
      </div>
      <div className="record-wrapper">
        <div className="record-arm" ref={armRef}></div>
        <div className="record-player" ref={recordRef} onClick={handleRecordClick}></div>
      </div>
    </div>
  );
};

export default SecretPage;